/**
 * CDAUR2Geocoder - Geocodificador usando CDAU desde R2
 *
 * Geocodifica direcciones usando el Callejero Digital de Andalucía Unificado
 * con datos precargados en Cloudflare R2 (2.69M portales).
 *
 * Nivel cascada: L8
 * Precisión: ±20-50m
 * CRS: EPSG:25830 (UTM30 ETRS89)
 *
 * @module services/geocoding/specialized
 * @version 1.0.0
 */

import { getCDAUR2Service, resetCDAUR2Service } from '../../cdau/CDAUR2Service';
import type { CDAUSearchResult, CDAUR2Result } from '../../../types/cdauR2';

// =============================================================================
// CONFIGURACIÓN
// =============================================================================

/** Configuración de scoring */
const SCORE_CONFIG = {
  exact: 92,        // Match exacto (score >= 0.95)
  fuzzyHigh: 82,    // Match alto (score >= 0.8)
  fuzzyMedium: 72,  // Match medio (score >= 0.6)
  fuzzyLow: 62,     // Match bajo (score < 0.6)
  numeroBonus: 5,   // Bonus si coincide número exacto
  cpBonus: 3,       // Bonus si coincide código postal
};

/** Precisión estimada en metros según calidad del match */
const PRECISION_CONFIG = {
  exact: 20,
  fuzzyHigh: 30,
  fuzzyMedium: 40,
  fuzzyLow: 50,
};

// =============================================================================
// SINGLETON
// =============================================================================

let geocoderInstance: CDAUR2Geocoder | null = null;

export function getCDAUR2Geocoder(): CDAUR2Geocoder {
  if (!geocoderInstance) {
    geocoderInstance = new CDAUR2Geocoder();
  }
  return geocoderInstance;
}

export function resetCDAUR2Geocoder(): void {
  geocoderInstance = null;
  resetCDAUR2Service();
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class CDAUR2Geocoder {
  private readonly service = getCDAUR2Service();

  /**
   * Geocodifica una dirección en un municipio
   *
   * @param direccion - Dirección a buscar (ej: "CALLE CORA 5")
   * @param codigoINE - Código INE del municipio (5 dígitos)
   * @param options - Opciones adicionales
   * @returns Resultado de geocodificación o null si no encuentra
   */
  async geocode(
    direccion: string,
    codigoINE: string,
    options?: {
      numero?: number;
      codigoPostal?: string;
      minScore?: number;
    }
  ): Promise<CDAUR2Result | null> {
    const results = await this.geocodeMultiple(direccion, codigoINE, {
      ...options,
      maxResults: 1,
    });

    return results.length > 0 ? results[0] : null;
  }

  /**
   * Geocodifica retornando múltiples resultados
   */
  async geocodeMultiple(
    direccion: string,
    codigoINE: string,
    options?: {
      numero?: number;
      codigoPostal?: string;
      maxResults?: number;
      minScore?: number;
    }
  ): Promise<CDAUR2Result[]> {
    const {
      numero,
      codigoPostal,
      maxResults = 5,
      minScore = 0.6,
    } = options ?? {};

    if (!direccion || direccion.trim().length < 2) {
      return [];
    }

    if (!/^\d{5}$/.test(codigoINE)) {
      console.warn(`[CDAUR2Geocoder] Código INE inválido: ${codigoINE}`);
      return [];
    }

    try {
      const searchResults = await this.service.search({
        codigoINE,
        direccion,
        numero,
        codigoPostal,
        maxResults: maxResults * 2, // Pedir más para filtrar
        threshold: minScore,
      });

      if (searchResults.length === 0) {
        return [];
      }

      return searchResults
        .slice(0, maxResults)
        .map(result => this.buildResult(result, numero, codigoPostal));
    } catch (error) {
      console.error(`[CDAUR2Geocoder] Error geocodificando "${direccion}":`, error);
      return [];
    }
  }

  /**
   * Construye el resultado de geocodificación
   */
  private buildResult(
    searchResult: CDAUSearchResult,
    requestedNumero?: number,
    requestedCP?: string
  ): CDAUR2Result {
    const { portal, score, matchedAddress: _matchedAddress } = searchResult;

    // Determinar score base y precisión según calidad del match
    let baseScore: number;
    let precision: number;

    if (score >= 0.95) {
      baseScore = SCORE_CONFIG.exact;
      precision = PRECISION_CONFIG.exact;
    } else if (score >= 0.8) {
      baseScore = SCORE_CONFIG.fuzzyHigh;
      precision = PRECISION_CONFIG.fuzzyHigh;
    } else if (score >= 0.6) {
      baseScore = SCORE_CONFIG.fuzzyMedium;
      precision = PRECISION_CONFIG.fuzzyMedium;
    } else {
      baseScore = SCORE_CONFIG.fuzzyLow;
      precision = PRECISION_CONFIG.fuzzyLow;
    }

    // Aplicar bonus
    if (requestedNumero !== undefined && portal.n === requestedNumero) {
      baseScore += SCORE_CONFIG.numeroBonus;
    }
    if (requestedCP && portal.cp === requestedCP) {
      baseScore += SCORE_CONFIG.cpBonus;
    }

    // Construir dirección completa
    const fullAddress = `${portal.t} ${portal.v}, ${portal.n} - ${portal.cp}`;

    return {
      x: portal.x,
      y: portal.y,
      confidence: Math.min(100, Math.round(baseScore)),
      precision,
      source: 'cdau_r2',
      sourceLevel: 8,
      portal,
      fullAddress,
      fuzzyScore: score,
    };
  }

  /**
   * Verifica si hay datos cargados para un municipio
   */
  hasMunicipio(codigoINE: string): boolean {
    return this.service.hasMunicipio(codigoINE);
  }

  /**
   * Precarga datos de un municipio
   */
  async preloadMunicipio(codigoINE: string): Promise<boolean> {
    const data = await this.service.loadMunicipio(codigoINE);
    return data !== null;
  }

  /**
   * Estadísticas del servicio
   */
  getStats(): { cacheSize: number; maxCacheSize: number; municipios: string[] } {
    const stats = this.service.getCacheStats();
    return {
      cacheSize: stats.size,
      maxCacheSize: stats.maxSize,
      municipios: stats.municipios,
    };
  }
}

// =============================================================================
// HELPER PARA ORCHESTRATOR
// =============================================================================

/**
 * Función helper compatible con GeocodingOrchestrator
 */
export async function geocodeWithCdauR2(
  direccion: string,
  codigoINE: string,
  options?: { numero?: number; codigoPostal?: string }
): Promise<{
  x: number;
  y: number;
  confidence: number;
  source: string;
  sourceLevel: number;
  matchedAddress: string;
  precision: number;
} | null> {
  const geocoder = getCDAUR2Geocoder();

  const result = await geocoder.geocode(direccion, codigoINE, options);

  if (!result) return null;

  return {
    x: result.x,
    y: result.y,
    confidence: result.confidence,
    source: 'cdau_r2',
    sourceLevel: 8,
    matchedAddress: result.fullAddress,
    precision: result.precision,
  };
}

export { CDAUR2Geocoder as default };
